import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { LifeBuoy, Plus } from "lucide-react";
import { TICKET_STATUSES, PRIORITIES, statusMeta } from "@/lib/taskman";

export default function TicketsPage() {
  const [tickets, setTickets] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientFilter, setClientFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ client_id: "", project_id: "", subject: "", description: "", priority: "normal" });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const params = {};
      if (clientFilter !== "all") params.client_id = clientFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      const [t, c, p] = await Promise.all([
        api.get("/tickets", { params }), api.get("/clients"), api.get("/projects"),
      ]);
      setTickets(t.data.tickets || []);
      setClients(Array.isArray(c.data) ? c.data : []);
      setProjects(p.data.projects || []);
    } catch { toast.error("Failed to load tickets"); }
    finally { setLoading(false); }
  }, [clientFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const createTicket = async () => {
    if (!form.client_id || !form.subject.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post("/tickets", {
        ...form, subject: form.subject.trim(),
        project_id: form.project_id || null,
      });
      toast.success("Ticket created");
      setCreateOpen(false);
      setForm({ client_id: "", project_id: "", subject: "", description: "", priority: "normal" });
      navigate(`/tickets/${data.ticket_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setSaving(false); }
  };

  const destProjects = projects.filter((p) => p.client_id === form.client_id && !p.is_default);

  return (
    <div className="p-6 space-y-4 max-w-[1400px]" data-testid="tickets-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-display font-extrabold tracking-tight flex items-center gap-2">
            <LifeBuoy className="h-6 w-6 text-[#002FA7]" /> Tickets
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Work requests — standalone against a client or attached to a project. Each ticket can hold its own task tree.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="new-ticket-btn">
          <Plus className="h-4 w-4 mr-1" /> New ticket
        </Button>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Select value={clientFilter} onValueChange={setClientFilter}>
          <SelectTrigger className="w-56" data-testid="ticket-filter-client">
            <SelectValue placeholder="All clients" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All clients</SelectItem>
            {clients.map((c) => (
              <SelectItem key={c.client_id} value={c.client_id}>{c.company_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48" data-testid="ticket-filter-status">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {TICKET_STATUSES.map((s) => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="p-10 text-center text-sm text-zinc-400">Loading…</div>
      ) : tickets.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-sm text-zinc-400" data-testid="tickets-empty">
          No tickets yet.
        </CardContent></Card>
      ) : (
        <Card><CardContent className="p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-400 border-b border-zinc-200">
                <th className="px-4 py-2">Subject</th>
                <th className="px-4 py-2">Client</th>
                <th className="px-4 py-2">Project</th>
                <th className="px-4 py-2">Priority</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Tasks</th>
                <th className="px-4 py-2">Raised by</th>
                <th className="px-4 py-2">Created</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((t) => {
                const st = statusMeta(TICKET_STATUSES, t.status);
                const pr = statusMeta(PRIORITIES, t.priority);
                const tc = t.task_counts || { total: 0, done: 0 };
                return (
                  <tr key={t.ticket_id} className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer"
                    onClick={() => navigate(`/tickets/${t.ticket_id}`)}
                    data-testid={`ticket-row-${t.ticket_id}`}>
                    <td className="px-4 py-2.5 font-medium text-zinc-800">
                      <Link to={`/tickets/${t.ticket_id}`} onClick={(e) => e.stopPropagation()}
                        className="hover:underline">{t.subject}</Link>
                    </td>
                    <td className="px-4 py-2.5 text-zinc-500">{t.client_name}</td>
                    <td className="px-4 py-2.5 text-zinc-500">{t.project_name || "—"}</td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex px-1.5 py-0.5 rounded-sm border text-[10px] font-semibold uppercase ${pr.cls}`}>{pr.label}</span>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex px-1.5 py-0.5 rounded-sm border text-[10px] font-semibold uppercase ${st.cls}`}>{st.label}</span>
                    </td>
                    <td className="px-4 py-2.5 text-zinc-500">{tc.done}/{tc.total}</td>
                    <td className="px-4 py-2.5 text-zinc-500 text-xs">
                      {t.created_by?.name || t.created_by?.email || "—"}
                      {t.created_by?.role === "client" && <span className="ml-1 text-[9px] uppercase text-violet-600 font-semibold">client</span>}
                    </td>
                    <td className="px-4 py-2.5 text-zinc-400 text-xs">{(t.created_at || "").slice(0, 10)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent></Card>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="new-ticket-dialog">
          <DialogHeader>
            <DialogTitle>New ticket</DialogTitle>
            <DialogDescription className="text-xs">A request for work — standalone or attached to a project.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Client</Label>
              <Select value={form.client_id}
                onValueChange={(v) => setForm((f) => ({ ...f, client_id: v, project_id: "" }))}>
                <SelectTrigger data-testid="ticket-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c.client_id} value={c.client_id}>{c.company_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Attach to project (optional)</Label>
              <Select value={form.project_id}
                onValueChange={(v) => setForm((f) => ({ ...f, project_id: v }))}>
                <SelectTrigger data-testid="ticket-project-select"><SelectValue placeholder="Standalone ticket" /></SelectTrigger>
                <SelectContent>
                  {destProjects.map((p) => (
                    <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Subject</Label>
              <Input value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                data-testid="ticket-subject-input" />
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea rows={3} value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                data-testid="ticket-description-input" />
            </div>
            <div>
              <Label className="text-xs">Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm((f) => ({ ...f, priority: v }))}>
                <SelectTrigger data-testid="ticket-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={createTicket} disabled={saving || !form.client_id || !form.subject.trim()}
              data-testid="ticket-create-btn">
              {saving ? "Creating…" : "Create ticket"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
